import { createElement, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Bold, Italic, Link2, Link2Off, List, ListOrdered, Underline } from 'lucide-react';
import { htmlDeTexto, sanitizarHtmlRico } from '@malgoos/shared';
import { Botao } from './base';
import { Entrada } from './formulario';

/**
 * Lote crm4 (L8 e L9, F4 e F8): editor com formatação do CRM (corpo do e-mail da negociação, modelos de e-mail e os blocos de
 * apresentação, condições e rodapé da proposta). Negrito, itálico, sublinhado, listas e link, como a barra da origem, nas
 * cores da Malgo. O HTML é DADO: o que sai do editor passa pelo `sanitizarHtmlRico` (o mesmo do servidor, que limpa de novo
 * antes de gravar e de enviar), o colado entra limpo e nada é montado por `innerHTML`: o conteúdo entra por nós do
 * `DOMParser` (documento inerte, sem script) e a leitura (`TextoRico`) vira elementos do React.
 */

/** Nós do HTML (já limpo) num documento inerte, para pôr no editor sem `innerHTML`. */
function nosDoHtml(html: string): Node[] {
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${html}</body>`, 'text/html');
  return Array.from(doc.body.childNodes);
}

const VAZIAS = new Set(['br', 'hr', 'img', 'wbr', 'input', 'col', 'area', 'source']);
const escTexto = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escAtributo = (v: string) => escTexto(v).replace(/"/g, '&quot;');
/**
 * HTML dos nós do editor, montado à mão a partir da árvore (sem ler `innerHTML`, que o teste dos sumidouros veda): só texto
 * escapado e etiquetas com os atributos como estão. O resultado ainda passa pelo `sanitizarHtmlRico`, que decide o que fica.
 */
function htmlDosNos(pai: Node): string {
  let saida = '';
  for (const n of Array.from(pai.childNodes)) {
    if (n.nodeType === Node.TEXT_NODE) saida += escTexto(n.textContent ?? '');
    else if (n.nodeType === Node.ELEMENT_NODE) {
      const e = n as Element;
      const nome = e.localName;
      const atributos = Array.from(e.attributes)
        .map((x) => ` ${x.name}="${escAtributo(x.value)}"`)
        .join('');
      saida += VAZIAS.has(nome) ? `<${nome}${atributos}>` : `<${nome}${atributos}>${htmlDosNos(e)}</${nome}>`;
    }
  }
  return saida;
}

const ESQUEMA = /^(https?:|mailto:|tel:)/i;
/** Link digitado: sem esquema vira https; só http(s), mailto e tel (o servidor confere de novo). */
export function normalizarLink(bruto: string): string | null {
  const v = bruto.trim();
  if (!v) return null;
  if (ESQUEMA.test(v)) return v;
  if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return null;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? `mailto:${v}` : `https://${v.replace(/^\/+/, '')}`;
}

type Comando = 'bold' | 'italic' | 'underline' | 'insertUnorderedList' | 'insertOrderedList';
const BOTOES: { cmd: Comando; icone: ReactNode; chave: string }[] = [
  { cmd: 'bold', icone: <Bold className="size-4" aria-hidden />, chave: 'negrito' },
  { cmd: 'italic', icone: <Italic className="size-4" aria-hidden />, chave: 'italico' },
  { cmd: 'underline', icone: <Underline className="size-4" aria-hidden />, chave: 'sublinhado' },
  { cmd: 'insertUnorderedList', icone: <List className="size-4" aria-hidden />, chave: 'marcadores' },
  { cmd: 'insertOrderedList', icone: <ListOrdered className="size-4" aria-hidden />, chave: 'numerada' },
];
const CLASSE_BOTAO = 'inline-flex size-8 items-center justify-center rounded text-tinta-2 hover:bg-sup-3 aria-pressed:bg-sup-3 aria-pressed:text-acento-forte disabled:opacity-50';

export function EditorRico({
  id,
  rotulo,
  valor,
  aoMudar,
  linhas = 6,
  desabilitado,
  dado,
}: {
  id: string;
  /** Nome acessível da área (o mesmo do `Rotulo` de fora). */
  rotulo: string;
  /** HTML do editor; texto antigo deve chegar já por `htmlDeTexto`. */
  valor: string;
  aoMudar: (html: string) => void;
  linhas?: number;
  desabilitado?: boolean;
  /** Valor do `data-editor-rico` (testes e provas). */
  dado?: string;
}) {
  const { t } = useTranslation();
  const area = useRef<HTMLDivElement>(null);
  const ultimo = useRef<string | null>(null);
  const selecao = useRef<Range | null>(null);
  const [ativos, setAtivos] = useState<Partial<Record<Comando, boolean>>>({});
  const [link, setLink] = useState<string | null>(null);

  // O valor de fora só entra quando não é o que o próprio editor acabou de emitir (não mexe no cursor enquanto digita).
  useEffect(() => {
    const el = area.current;
    if (!el || valor === ultimo.current) return;
    const limpo = sanitizarHtmlRico(valor);
    ultimo.current = limpo;
    el.replaceChildren(...nosDoHtml(limpo));
  }, [valor]);

  useEffect(() => {
    const aoMudarSelecao = () => {
      const el = area.current;
      const s = document.getSelection();
      if (!el || !s?.anchorNode || !el.contains(s.anchorNode)) return;
      setAtivos(Object.fromEntries(BOTOES.map((b) => [b.cmd, document.queryCommandState(b.cmd)])));
    };
    document.addEventListener('selectionchange', aoMudarSelecao);
    return () => document.removeEventListener('selectionchange', aoMudarSelecao);
  }, []);

  const emitir = () => {
    const el = area.current;
    if (!el) return;
    const limpo = sanitizarHtmlRico(htmlDosNos(el));
    ultimo.current = limpo;
    aoMudar(limpo);
  };
  const executar = (cmd: string, arg?: string) => {
    area.current?.focus();
    document.execCommand(cmd, false, arg);
    emitir();
  };
  const guardarSelecao = () => {
    const s = document.getSelection();
    selecao.current = s && s.rangeCount && area.current?.contains(s.anchorNode) ? s.getRangeAt(0).cloneRange() : null;
  };
  /** Fecha a barra do link (Esc ou Cancelar) e devolve o foco ao texto do editor (rcrm4 VIS-M1). */
  const fecharLink = () => {
    setLink(null);
    area.current?.focus();
  };
  const aplicarLink = () => {
    const url = normalizarLink(link ?? '');
    setLink(null);
    area.current?.focus();
    const r = selecao.current;
    if (r) {
      const s = document.getSelection();
      s?.removeAllRanges();
      s?.addRange(r);
    }
    if (!url) return;
    if (!r || r.collapsed) {
      // Sem texto selecionado, o próprio endereço vira o texto do link (escapado pelo sanitizador do HTML inserido).
      document.execCommand('insertHTML', false, sanitizarHtmlRico(`<a href="${url.replace(/"/g, '&quot;')}">${url.replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' })[c]!)}</a>`));
    } else document.execCommand('createLink', false, url);
    emitir();
  };

  return (
    <div className="flex min-w-0 flex-col rounded-lg border border-borda-campo bg-campo focus-within:border-foco focus-within:outline-2 focus-within:outline-foco" data-editor-rico={dado ?? ''}>
      <div role="toolbar" aria-label={t('editorRico.barra', { campo: rotulo })} aria-controls={id} className="flex flex-wrap items-center gap-0.5 border-b border-borda px-1.5 py-1">
        {BOTOES.map((b) => (
          <button
            key={b.cmd}
            type="button"
            className={CLASSE_BOTAO}
            disabled={desabilitado}
            aria-pressed={!!ativos[b.cmd]}
            aria-label={t(`editorRico.${b.chave}`)}
            title={t(`editorRico.${b.chave}`)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => executar(b.cmd)}
            data-formatar={b.cmd}
          >
            {b.icone}
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-borda" aria-hidden />
        <button
          type="button"
          className={CLASSE_BOTAO}
          disabled={desabilitado}
          aria-label={t('editorRico.link')}
          title={t('editorRico.link')}
          aria-expanded={link !== null}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            guardarSelecao();
            setLink('');
          }}
          data-formatar="link"
        >
          <Link2 className="size-4" aria-hidden />
        </button>
        <button type="button" className={CLASSE_BOTAO} disabled={desabilitado} aria-label={t('editorRico.tirarLink')} title={t('editorRico.tirarLink')} onMouseDown={(e) => e.preventDefault()} onClick={() => executar('unlink')} data-formatar="unlink">
          <Link2Off className="size-4" aria-hidden />
        </button>
      </div>
      {link !== null && (
        <div className="flex flex-wrap items-center gap-2 border-b border-borda px-2 py-2" data-editor-link="">
          <Entrada
            autoFocus
            className="min-w-48 flex-1"
            aria-label={t('editorRico.endereco')}
            placeholder={t('editorRico.enderecoExemplo')}
            value={link}
            maxLength={2000}
            onChange={(e) => setLink(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                aplicarLink();
              } else if (e.key === 'Escape') {
                // rcrm4 VIS-M1: o Esc fecha só a barra do link; sem parar a propagação, o vigia da camada fechava o e-mail inteiro.
                e.preventDefault();
                e.stopPropagation();
                fecharLink();
              }
            }}
            data-editor-link-url=""
          />
          <Botao tamanho="sm" variante="fantasma" onClick={fecharLink} data-editor-link-cancelar="">
            {t('comum.cancelar')}
          </Botao>
          <Botao tamanho="sm" variante="primario" disabled={!normalizarLink(link)} onClick={aplicarLink} data-editor-link-aplicar="">
            {t('editorRico.aplicar')}
          </Botao>
        </div>
      )}
      <div
        ref={area}
        id={id}
        role="textbox"
        aria-multiline="true"
        aria-label={rotulo}
        aria-disabled={desabilitado || undefined}
        contentEditable={!desabilitado}
        suppressContentEditableWarning
        className="texto-rico min-w-0 overflow-y-auto px-3 py-2 text-base text-tinta-1 outline-none md:text-sm"
        style={{ minHeight: `${linhas * 1.5}rem`, maxHeight: '28rem' }}
        onFocus={() => document.execCommand('defaultParagraphSeparator', false, 'p')}
        onInput={emitir}
        onBlur={emitir}
        onPaste={(e) => {
          // O colado entra limpo: o HTML pela mesma lista fechada; sem HTML, o texto como parágrafos escapados.
          e.preventDefault();
          const html = e.clipboardData.getData('text/html');
          const limpo = html ? sanitizarHtmlRico(html) : htmlDeTexto(e.clipboardData.getData('text/plain'));
          if (limpo) document.execCommand('insertHTML', false, limpo);
          emitir();
        }}
        onDrop={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
            e.preventDefault();
            guardarSelecao();
            setLink('');
          }
        }}
      />
    </div>
  );
}

const ETIQUETAS = new Set(['p', 'br', 'div', 'strong', 'em', 'u', 's', 'ul', 'ol', 'li', 'a', 'blockquote', 'span']);

/** Elementos do React a partir do HTML (já limpo de novo aqui); `texto` formata cada trecho (modo discreto). */
function paraReact(nos: ArrayLike<Node>, texto: (s: string) => string, chave = 'n'): ReactNode[] {
  return Array.from(nos).map((n, i): ReactNode => {
    const k = `${chave}-${i}`;
    if (n.nodeType === Node.TEXT_NODE) return texto(n.textContent ?? '');
    if (n.nodeType !== Node.ELEMENT_NODE) return null;
    const el = n as Element;
    const tag = el.tagName.toLowerCase();
    if (!ETIQUETAS.has(tag)) return paraReact(el.childNodes, texto, k);
    const props: Record<string, unknown> = { key: k };
    const alinhamento = /text-align:\s*(left|center|right|justify)/.exec(el.getAttribute('style') ?? '')?.[1];
    if (alinhamento) props.style = { textAlign: alinhamento };
    if (tag === 'a') {
      const href = el.getAttribute('href');
      if (href && ESQUEMA.test(href)) Object.assign(props, { href, target: '_blank', rel: 'noopener noreferrer nofollow' });
    }
    if (tag === 'br') return createElement('br', props);
    return createElement(tag, props, ...paraReact(el.childNodes, texto, k));
  });
}

const IGUAL = (s: string) => s;

/** Leitura do texto formatado (prévia e impressão da proposta, e-mail enviado, prévia do modelo). */
export function TextoRico({ html, className = '', texto = IGUAL, ...resto }: { html: string; className?: string; texto?: (s: string) => string } & Record<`data-${string}`, string>) {
  const filhos = useMemo(() => paraReact(nosDoHtml(sanitizarHtmlRico(html)), texto), [html, texto]);
  return (
    <div className={`texto-rico ${className}`} {...resto}>
      {filhos}
    </div>
  );
}

/** O HTML para o editor ou a leitura: o gravado, ou o texto de antes do editor como parágrafos. */
export const htmlDoCampo = (html: string | null | undefined, texto: string) => (html ? html : htmlDeTexto(texto));
